import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SelectInput from "../../../../components/inputs/SelectInput";
import FractionInput, { formatLatexPreview } from "../../../../components/inputs/FractionInput";
import GraphViewer from "../../../../components/GraphPlotter/GraphViewer";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickOne(arr) {
  return arr[randInt(0, arr.length - 1)];
}

function parseVal(v) {
  if (v && typeof v === "object" && Number.isFinite(v.parsed)) return v.parsed;
  return NaN;
}

function isBlank(v) {
  return !v || !v.raw || String(v.raw).trim() === "";
}

function fmtSigned(n) {
  return n >= 0 ? `+ ${n}` : `- ${Math.abs(n)}`;
}

function polyLatex(a, b, c) {
  const terms = [];
  const pushTerm = (coef, body) => {
    if (coef === 0) return;
    const absCoef = Math.abs(coef);
    const coefPart = absCoef === 1 ? "" : `${absCoef}`;
    const core = `${coefPart}${body}`;
    if (terms.length === 0) {
      terms.push(coef < 0 ? `-${core}` : core);
    } else {
      terms.push(coef < 0 ? `- ${core}` : `+ ${core}`);
    }
  };

  pushTerm(a, "x^2");
  pushTerm(b, "x");

  if (c !== 0) {
    if (terms.length === 0) terms.push(`${c}`);
    else terms.push(c < 0 ? `- ${Math.abs(c)}` : `+ ${c}`);
  }

  if (terms.length === 0) terms.push("0");
  return `y = ${terms.join(" ")}`;
}

function lineLatex(a, b) {
  if (a === 0) return `y = ${b}`;
  const aPart = a === 1 ? "x" : a === -1 ? "-x" : `${a}x`;
  if (b === 0) return `y = ${aPart}`;
  return `y = ${aPart} ${b > 0 ? "+" : "-"} ${Math.abs(b)}`;
}

function polyExpr(a, b, c) {
  const parts = [`${a}x^2`];
  if (b !== 0) parts.push(b > 0 ? `+${b}x` : `${b}x`);
  if (c !== 0) parts.push(c > 0 ? `+${c}` : `${c}`);
  return `y=${parts.join("")}`;
}

function lineExpr(a, b) {
  if (a === 0) return `y=${b}`;
  const parts = [`${a}x`];
  if (b !== 0) parts.push(b > 0 ? `+${b}` : `${b}`);
  return `y=${parts.join("")}`;
}

function buildWindow(center, span = 10) {
  // Stałe okno 10x10, ale przesuwane tak, aby:
  // - osie x=0 i y=0 zawsze były wewnątrz wykresu (nie na krawędzi),
  // - była minimum 1 kratka zapasu od brzegu.
  let start = Math.round(center) - span / 2;
  start = Math.max(-9, Math.min(-1, start));
  const end = start + span;
  return [start, end];
}

function makeTaskData() {
  // 2 rozw: 1/2, 1 rozw: 1/3, 0 rozw: 1/6
  const r = Math.random();
  const mode = r < 0.5 ? 2 : r < 5 / 6 ? 1 : 0;

  for (let t = 0; t < 300; t += 1) {
    const A = pickOne([-2, -1, 1, 2]);
    const la = randInt(-3, 3);
    const lb = randInt(-4, 4);

    let B;
    let C;
    let points = [];

    if (mode === 2) {
      const x1 = randInt(-4, 4);
      const x2 = randInt(-4, 4);
      if (x1 === x2) continue;

      B = -A * (x1 + x2);
      C = A * x1 * x2;

      const y1 = la * x1 + lb;
      const y2 = la * x2 + lb;
      points = [
        { x: x1, y: y1 },
        { x: x2, y: y2 },
      ].sort((p, q) => p.x - q.x);
    } else if (mode === 1) {
      const x0 = randInt(-4, 4);
      B = -2 * A * x0;
      C = A * x0 * x0;

      const y0 = la * x0 + lb;
      points = [{ x: x0, y: y0 }];
    } else {
      const h = randInt(-3, 3);
      const kAbs = randInt(1, 4);
      const k = A > 0 ? kAbs : -kAbs; // delta < 0
      B = -2 * A * h;
      C = A * h * h + k;
      points = [];
    }

    const qa = A;
    const qb = la + B;
    const qc = lb + C;

    if (Math.abs(qb) > 12 || Math.abs(qc) > 15) continue;

    // Dynamiczny kadr 10x10 pod punkty charakterystyczne
    const vx = -qb / (2 * qa);
    const keyX = points.length ? points.map((p) => p.x) : [vx, vx - 2, vx + 2];
    const minX = Math.min(...keyX);
    const maxX = Math.max(...keyX);
    const centerX = (minX + maxX) / 2;
    const xRange = buildWindow(centerX, 10);

    const yAtP = (x) => qa * x * x + qb * x + qc;
    const yAtL = (x) => la * x + lb;

    const yCandidates = [
      ...keyX.map((x) => yAtP(x)),
      ...keyX.map((x) => yAtL(x)),
      yAtP(vx),
      yAtL(vx),
      yAtP(xRange[0]),
      yAtP(xRange[1]),
      yAtL(xRange[0]),
      yAtL(xRange[1]),
      0,
    ];

    const yMin = Math.min(...yCandidates);
    const yMax = Math.max(...yCandidates);
    const centerY = (yMin + yMax) / 2;
    const yRange = buildWindow(centerY, 10);

    // Czytelność: punkty przecięcia i charakterystyczne wartości w bezpiecznym obszarze.
    const inY = (y) => y >= yRange[0] - 0.5 && y <= yRange[1] + 0.5;
    if (!inY(yAtP(vx))) continue;
    if (points.some((p) => !inY(p.y))) continue;

    // Punkty przecięcia mają wpadać do kwadratu [-8, 8] x [-8, 8].
    if (points.some((p) => Math.abs(p.x) > 8 || Math.abs(p.y) > 8)) continue;

    // I dodatkowo mieć min. 1 kratkę zapasu od brzegu aktualnego okna 10x10.
    const xInnerMin = xRange[0] + 1;
    const xInnerMax = xRange[1] - 1;
    const yInnerMin = yRange[0] + 1;
    const yInnerMax = yRange[1] - 1;
    if (
      points.some(
        (p) =>
          p.x < xInnerMin ||
          p.x > xInnerMax ||
          p.y < yInnerMin ||
          p.y > yInnerMax
      )
    ) {
      continue;
    }

    // Ograniczenia wspĂłĹ‚czynnikĂłw
    if (Math.abs(la) > 4 || Math.abs(lb) > 8) continue;
    if (Math.abs(qa) > 2 || Math.abs(qb) > 12 || Math.abs(qc) > 18) continue;

    return {
      qa,
      qb,
      qc,
      la,
      lb,
      points,
      solutionCount: mode,
      parabolaLatex: polyLatex(qa, qb, qc),
      lineLatex: lineLatex(la, lb),
      xRange,
      yRange,
    };
  }

  return {
    qa: 1,
    qb: -1,
    qc: -2,
    la: 1,
    lb: 0,
    points: [
      { x: -1, y: -1 },
      { x: 2, y: 2 },
    ],
    solutionCount: 2,
    parabolaLatex: "y = x^2 - x - 2",
    lineLatex: "y = x",
    xRange: [-6, 6],
    yRange: [-6, 6],
  };
}

export function generateTask() {
  return {
    type: "LinearQuadraticGraphIntersectionTask",
    data: makeTaskData(),
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>Wyznacz punkty przecięcia paraboli i prostej:</p>
      <BlockMath math={data.parabolaLatex} />
      <BlockMath math={data.lineLatex} />
      <p>Najpierw wybierz liczbę rozwiązań, potem podaj odpowiednie pary.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer = {}, onChange, disabled) {
  const count = userAnswer?.count ?? "";
  return (
    <div className="flex flex-col gap-3">
      <SelectInput
        options={[
          { value: "0", label: "brak rozwiązań" },
          { value: "1", label: "jedno rozwiązanie" },
          { value: "2", label: "dwa rozwiązania" },
        ]}
        value={count}
        onChange={(e) => onChange({ ...userAnswer, count: e?.target?.value ?? e })}
        placeholder="Wybierz liczbę rozwiązań"
        disabled={disabled}
      />

      {(count === "1" || count === "2") && (
        <>
          <div className="flex items-center gap-2">
            <InlineMath math={"x_1 ="} />
            <FractionInput
              value={userAnswer?.x1 || ""}
              onChange={(v) => onChange({ ...userAnswer, x1: v })}
              disabled={disabled}
              label="x_1"
              placeholder="np. -1"
            />
          </div>
          <div className="flex items-center gap-2">
            <InlineMath math={"y_1 ="} />
            <FractionInput
              value={userAnswer?.y1 || ""}
              onChange={(v) => onChange({ ...userAnswer, y1: v })}
              disabled={disabled}
              label="y_1"
              placeholder="np. 2"
            />
          </div>
        </>
      )}

      {count === "2" && (
        <>
          <div className="flex items-center gap-2">
            <InlineMath math={"x_2 ="} />
            <FractionInput
              value={userAnswer?.x2 || ""}
              onChange={(v) => onChange({ ...userAnswer, x2: v })}
              disabled={disabled}
              label="x_2"
              placeholder="np. 3"
            />
          </div>
          <div className="flex items-center gap-2">
            <InlineMath math={"y_2 ="} />
            <FractionInput
              value={userAnswer?.y2 || ""}
              onChange={(v) => onChange({ ...userAnswer, y2: v })}
              disabled={disabled}
              label="y_2"
              placeholder="np. -4"
            />
          </div>
        </>
      )}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const eps = 0.01;
  const count = data.solutionCount;
  const picked = userAnswer?.count;

  if (picked !== String(count)) return { correct: false };

  const x1Blank = isBlank(userAnswer?.x1);
  const y1Blank = isBlank(userAnswer?.y1);
  const x2Blank = isBlank(userAnswer?.x2);
  const y2Blank = isBlank(userAnswer?.y2);

  const ux1 = parseVal(userAnswer?.x1);
  const uy1 = parseVal(userAnswer?.y1);
  const ux2 = parseVal(userAnswer?.x2);
  const uy2 = parseVal(userAnswer?.y2);

  if (count === 0) {
    return { correct: x1Blank && y1Blank && x2Blank && y2Blank };
  }

  if (count === 1) {
    const p = data.points[0];
    const pair1Valid =
      Number.isFinite(ux1) &&
      Number.isFinite(uy1) &&
      Math.abs(ux1 - p.x) < eps &&
      Math.abs(uy1 - p.y) < eps;
    const pair2Valid =
      Number.isFinite(ux2) &&
      Number.isFinite(uy2) &&
      Math.abs(ux2 - p.x) < eps &&
      Math.abs(uy2 - p.y) < eps;

    if (pair1Valid && x2Blank && y2Blank) return { correct: true };
    if (pair2Valid && x1Blank && y1Blank) return { correct: true };
    if (pair1Valid && pair2Valid) return { correct: true };
    return { correct: false };
  }

  if (
    !Number.isFinite(ux1) ||
    !Number.isFinite(uy1) ||
    !Number.isFinite(ux2) ||
    !Number.isFinite(uy2)
  ) {
    return { correct: false };
  }

  const pA = data.points[0];
  const pB = data.points[1];

  const direct =
    Math.abs(ux1 - pA.x) < eps &&
    Math.abs(uy1 - pA.y) < eps &&
    Math.abs(ux2 - pB.x) < eps &&
    Math.abs(uy2 - pB.y) < eps;

  const swapped =
    Math.abs(ux1 - pB.x) < eps &&
    Math.abs(uy1 - pB.y) < eps &&
    Math.abs(ux2 - pA.x) < eps &&
    Math.abs(uy2 - pA.y) < eps;

  return { correct: direct || swapped };
}

export function renderAnswer({ data }) {
  const countLabel =
    data.solutionCount === 0
      ? "brak rozwiązań"
      : data.solutionCount === 1
      ? "jedno rozwiązanie"
      : "dwa rozwiązania";

  if (data.solutionCount === 0) {
    return (
      <div style={{ textAlign: "left", lineHeight: 1.35 }}>
        <div>Liczba rozwiązań: {countLabel}</div>
        <div><InlineMath math={"\\varnothing"} /></div>
      </div>
    );
  }

  if (data.solutionCount === 1) {
    const p = data.points[0];
    return (
      <div style={{ textAlign: "left", lineHeight: 1.35 }}>
        <div style={{ marginBottom: "0.2rem" }}>Liczba rozwiązań: {countLabel}</div>
        <InlineMath math={`(x,y)=(${p.x},${p.y})`} />
      </div>
    );
  }

  return (
    <div style={{ textAlign: "left", lineHeight: 1.35 }}>
      <div style={{ marginBottom: "0.2rem" }}>Liczba rozwiązań: {countLabel}</div>
      <div><InlineMath math={`(x_1,y_1)=(${data.points[0].x},${data.points[0].y})`} /></div>
      <div><InlineMath math={`(x_2,y_2)=(${data.points[1].x},${data.points[1].y})`} /></div>
    </div>
  );
}

export function renderUserAnswer(taskOrUserAnswer, maybeUserAnswer) {
  const userAnswer =
    maybeUserAnswer && typeof maybeUserAnswer === "object"
      ? maybeUserAnswer
      : taskOrUserAnswer && typeof taskOrUserAnswer === "object"
      ? taskOrUserAnswer
      : {};

  const picked = userAnswer?.count;
  const rawOf = (v) => (v && typeof v === "object" && "raw" in v ? v.raw : v) || "";
  const hasAny =
    picked ||
    rawOf(userAnswer?.x1) ||
    rawOf(userAnswer?.y1) ||
    rawOf(userAnswer?.x2) ||
    rawOf(userAnswer?.y2);

  if (!hasAny) return <i>(brak Odpowiedźi)</i>;

  const x1 = rawOf(userAnswer?.x1) || "?";
  const y1 = rawOf(userAnswer?.y1) || "?";
  const x2 = rawOf(userAnswer?.x2) || "?";
  const y2 = rawOf(userAnswer?.y2) || "?";

  const showOne = picked === "1";
  const showTwo = picked === "2";

  return (
    <div style={{ textAlign: "left", lineHeight: 1.35 }}>
      <div style={{ marginBottom: "0.2rem" }}>
        {picked === "0"
          ? "Liczba rozwiązań: brak rozwiązań"
          : picked === "1"
          ? "Liczba rozwiązań: jedno rozwiązanie"
          : picked === "2"
          ? "Liczba rozwiązań: dwa rozwiązania"
          : "Liczba rozwiązań: (brak wyboru)"}
      </div>
      {showOne && (
        <>
          <div><InlineMath math={formatLatexPreview(x1, "x_1")} /></div>
          <div><InlineMath math={formatLatexPreview(y1, "y_1")} /></div>
        </>
      )}
      {showTwo && (
        <>
          <div><InlineMath math={formatLatexPreview(x1, "x_1")} /></div>
          <div><InlineMath math={formatLatexPreview(y1, "y_1")} /></div>
          <div><InlineMath math={formatLatexPreview(x2, "x_2")} /></div>
          <div><InlineMath math={formatLatexPreview(y2, "y_2")} /></div>
        </>
      )}
    </div>
  );
}

export function renderExplanation({ data }) {
  const { qa, qb, qc, la, lb, points } = data;
  const A = qa;
  const B = qb - la;
  const C = qc - lb;
  const delta = B * B - 4 * A * C;

  const parabola = polyExpr(qa, qb, qc);
  const line = lineExpr(la, lb);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p><strong>Krok 1:</strong> Zrównujemy prawe strony i przenosimy wszystko na jedną stronę.</p>
      <BlockMath math={`${qa}x^2 ${fmtSigned(qb)}x ${fmtSigned(qc)} = ${la}x ${lb >= 0 ? "+" : "-"} ${Math.abs(lb)}`} />
      <BlockMath math={`${A}x^2 ${fmtSigned(B)}x ${fmtSigned(C)} = 0`} />

      <p><strong>Krok 2:</strong> Liczymy deltę równania kwadratowego.</p>
      <BlockMath math={`\\Delta = (${B})^2 - 4\\cdot(${A})\\cdot(${C}) = ${delta}`} />

      {delta < 0 && (
        <>
          <p>Ponieważ <InlineMath math={"\\Delta < 0"} />, prosta i parabola nie maja punktów przecięcia.</p>
          <p><strong>Odpowiedź:</strong> <InlineMath math={"\\varnothing"} />.</p>
        </>
      )}

      {delta === 0 && (
        <>
          <BlockMath math={`x_0 = \\dfrac{-(${B})}{2\\cdot(${A})} = ${points[0].x}`} />
          <BlockMath math={`y_0 = ${la}\\cdot(${points[0].x}) ${lb >= 0 ? "+" : "-"} ${Math.abs(lb)} = ${points[0].y}`} />
          <p><strong>Odpowiedź:</strong> <InlineMath math={`(x,y)=(${points[0].x},${points[0].y})`} />.</p>
        </>
      )}

      {delta > 0 && (
        <>
          <BlockMath math={`x_1 = ${points[0].x},\\ x_2 = ${points[1].x}`} />
          <BlockMath math={`y_1 = ${la}\\cdot(${points[0].x}) ${lb >= 0 ? "+" : "-"} ${Math.abs(lb)} = ${points[0].y}`} />
          <BlockMath math={`y_2 = ${la}\\cdot(${points[1].x}) ${lb >= 0 ? "+" : "-"} ${Math.abs(lb)} = ${points[1].y}`} />
          <p>
            <strong>Odpowiedź:</strong>{" "}
            <InlineMath math={`\\{(${points[0].x},${points[0].y}),\\ (${points[1].x},${points[1].y})\\}`} />.
          </p>
        </>
      )}

      <div style={{ maxWidth: 350, margin: "18px auto 0" }}>
        <GraphViewer
          width={350}
          xRange={data.xRange}
          yRange={data.yRange}
          equations={[
            { expr: parabola, color: "#ff8c1a" },
            { expr: line, color: "#9d4edd" },
          ]}
          points={
            data.solutionCount === 0
              ? {}
              : data.solutionCount === 1
              ? {
                  P: {
                    x: points[0].x,
                    y: points[0].y,
                    label: "P",
                    color: "#cc5500",
                    textColor: "#cc5500",
                    textPosition: "top center",
                  },
                }
              : {
                  A: {
                    x: points[0].x,
                    y: points[0].y,
                    label: "A",
                    color: "#cc5500",
                    textColor: "#cc5500",
                    textPosition: "top center",
                  },
                  B: {
                    x: points[1].x,
                    y: points[1].y,
                    label: "B",
                    color: "#cc5500",
                    textColor: "#cc5500",
                    textPosition: "top center",
                  },
                }
          }
        />
      </div>
    </div>
  );
}

